import { createContext, ReactNode, useContext, useMemo } from 'react';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';

import { useSiteSettings } from '@gn-audio/jss-components/utils/contexts';
import {
  DEFAULT_SC_LOCALE,
  Family,
  getFamily,
  getFamilyAttributes,
  getFamilyPrice,
  getGroup,
  getGroupAttributes,
  getGroupPrice,
  getLocaleWithRegion,
  Group,
  isPricingApiConfigured,
  pricingHttp,
  productCatalogueHttp,
  ProductWithAttributes,
} from '@gn-audio/jss-core';

import { mapToStandardPriceModel } from 'api/pricesApi/priceMapper';
import { StandardPriceModel } from 'api/pricesApi/pricesModel';
import { getSharedProperty } from 'common/getters';
import { useAsync } from 'utils/hooks/useAsync';

export interface ICProductContext {
  productId?: string;
  productName?: string;
  productType?: string;
  attributes?: ProductWithAttributes | null;
  product?: Family | Group | null;
  price?: StandardPriceModel | null;
}
export const PageProductContext = createContext<ICProductContext>({});

export const usePageProduct = () => useContext(PageProductContext);

export const useProductField = () => {
  const { sitecoreContext } = useSitecoreContext();

  const product = sitecoreContext.route?.fields
    ? getSharedProperty(sitecoreContext.route?.fields as { Product: Field<string> }, 'Product')
        ?.value
    : undefined;

  const [productId, productType, productName] = (product ?? '').split(':');
  return { productId, productType, productName };
};

export const PageProductProvider = ({ children }: { children: ReactNode }) => {
  const { productId, productType, productName } = useProductField();
  const { ecom } = useSiteSettings();
  const { sitecoreContext } = useSitecoreContext();
  const language = getLocaleWithRegion(sitecoreContext.language ?? DEFAULT_SC_LOCALE);

  const attributes = useAsync(async () => {
    if (!productId || !productType) return null;
    if (productType === 'Group') {
      return getGroupAttributes(+productId, language);
    }
    if (productType === 'Family') {
      const result = await getFamilyAttributes(+productId, language);
      return result?.length ? result[0] : null;
    }
    return null;
  }, [productId, productType, productCatalogueHttp]);

  const product = useAsync(async () => {
    if (!productId || !productType) return null;
    if (productType === 'Group') {
      return getGroup(+productId, language);
    }
    if (productType === 'Family') {
      return await getFamily(+productId, language);
    }
    return null;
  }, [productId, productType, productCatalogueHttp]);

  const price = useAsync(async () => {
    if (!isPricingApiConfigured() || !productId || !productType || !ecom?.ecomEnabled) {
      return null;
    }
    let priceData;
    if (productType === 'Group') {
      priceData = await getGroupPrice(+productId);
    }
    if (productType === 'Family') {
      priceData = await getFamilyPrice(+productId);
    }
    return priceData ? mapToStandardPriceModel(priceData) : null;
  }, [productId, productType, ecom, pricingHttp]);

  const productContext = useMemo(() => {
    return { productId, productType, productName, product, attributes, price };
  }, [productId, productType, productName, attributes, product, price]);

  return (
    <PageProductContext.Provider value={productContext}>{children}</PageProductContext.Provider>
  );
};
